import SEO from "../seo/SEO";
import { 
  AlertTriangle, 
  BookOpen, 
  Link2, 
  DollarSign, 
  UserX, 
  Mail, 
  Globe, 
  FileText 
} from "lucide-react";

export default function Disclaimer() {
  return (
    <div className="space-y-12 py-12 max-w-4xl mx-auto px-4">
      <SEO 
        title="Disclaimer &amp; Disclosures - USMoneyAI" 
        description="Review the financial disclaimer and advertising disclosures of USMoneyAI. Learn about calculator estimate boundaries and professional relationship details."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Disclaimer", url: "/disclaimer" }
        ]}
      />

      {/* Header section */}
      <div className="space-y-3.5 text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/10 text-xs font-semibold text-amber-400 select-none">
          <AlertTriangle className="w-3.5 h-3.5" />
          Official Disclosures
        </div>
        <h1 className="text-3xl sm:text-4xl font-sans font-bold text-white tracking-tight">
          Disclaimer &amp; Disclosures
        </h1>
        <p className="text-slate-400 text-sm font-light">
          Last Updated: June 2026
        </p>
      </div>

      <div className="space-y-8 text-left">
        {/* Core disclaimer statement */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-amber-400 font-semibold text-lg">
            <BookOpen className="w-5 h-5 text-amber-500" />
            <h2>General Educational Purpose Only</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              The information and calculators available on <strong>USMoneyAI</strong> are provided for general educational purposes only.
            </p>
            <p>
              While we work hard to keep our tools accurate and up to date, we can&apos;t guarantee that every result will be correct for every situation. Financial decisions often depend on personal circumstances, market conditions, taxes, fees, and other factors that may not be reflected in calculator estimates.
            </p>
            <p className="text-amber-300 font-medium">
              The results generated by our calculators should be viewed as estimates, not guarantees.
            </p>
            <p>
              USMoneyAI is not a bank, lender, investment advisor, tax professional, law firm, or financial services provider. Nothing on this website should be considered financial, investment, tax, legal, or professional advice.
            </p>
            <p>
              Before making important financial decisions, it&apos;s always a good idea to speak with a qualified professional who understands your individual situation.
            </p>
          </div>
        </section>

        {/* Calculator Results section */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <FileText className="w-5 h-5 text-amber-500" />
            <h2>Calculator Results &amp; Variables</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed space-y-4 font-light">
            <p>
              Our calculators use commonly accepted financial formulas and assumptions to provide estimates.
            </p>
            <p className="font-medium text-slate-200">
              Actual results may be different because of:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-1 pt-1 text-[15px] text-slate-300 font-normal">
              {[
                "Changes in interest rates",
                "Taxes and fees",
                "Inflation",
                "Investment performance",
                "Loan terms",
                "Personal financial circumstances"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="pt-2 text-slate-400 text-sm italic">
              For that reason, calculator results should be used as planning tools rather than exact predictions.
            </p>
          </div>
        </section>

        {/* Third Party Links */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <Link2 className="w-5 h-5 text-amber-500" />
            <h2>Third-Party Links</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed font-light">
            <p>
              Some pages on USMoneyAI may contain links to third-party websites. 
              We provide these links for convenience and additional information. We do not control or take responsibility for the content, services, or privacy practices of external websites.
            </p>
          </div>
        </section>

        {/* Advertising Disclosure */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <DollarSign className="w-5 h-5 text-amber-500" />
            <h2>Advertising Disclosure</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed font-light">
            <p>
              USMoneyAI may display advertisements from third-party advertising partners, including Google AdSense. 
              Advertisements help support the operation and maintenance of this website. However, advertising relationships do not influence how our calculators work or how educational content is presented.
            </p>
          </div>
        </section>

        {/* No Professional Relationship */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2.5 text-white font-semibold text-lg">
            <UserX className="w-5 h-5 text-amber-500" />
            <h2>No Professional Relationship</h2>
          </div>
          <div className="text-slate-300 text-base leading-relaxed font-light">
            <p>
              Using this website does not create a financial advisor-client, attorney-client, accountant-client, or any other professional relationship. 
              All decisions made using information from this website remain the responsibility of the user.
            </p>
          </div>
        </section>

        {/* Contact details */}
        <section className="bg-[#0c1322] border border-[#1E293B] rounded-2xl p-6 sm:p-8 space-y-5">
          <h2 className="text-lg font-semibold text-white">Contact Us</h2>
          <p className="text-slate-300 text-base font-light">
            If you have questions about this disclaimer, you can contact us at:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <a 
              href="mailto:earningtab72@gmail.com" 
              className="flex items-center gap-3 p-4 rounded-xl bg-[#121c31]/50 border border-slate-800 hover:border-slate-700 hover:bg-[#121c31] transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-light font-mono">Email Support</div>
                <div className="text-[15px] font-medium text-white group-hover:text-amber-400 transition-colors">
                  earningtab72@gmail.com
                </div>
              </div>
            </a>
            
            <a 
              href="https://usmoneyai.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-3 p-4 rounded-xl bg-[#121c31]/50 border border-slate-800 hover:border-slate-700 hover:bg-[#121c31] transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-light font-mono">Official Website</div>
                <div className="text-[15px] font-medium text-white group-hover:text-amber-400 transition-colors">
                  usmoneyai.com
                </div>
              </div>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
